import React, { useEffect, useMemo, useState } from "react";
import { useNotification } from "../../../context/NotificationContext";
import { resolveMediaUrl } from "../../../utils/media";
import AdminEditorModal from "./AdminEditorModal";
import AdminCollapsibleSection from "./AdminCollapsibleSection";
import AdminProductListCard from "./AdminProductListCard";
import {
  deleteAdminSector,
  deleteAdminSectorGalleryImage,
  deleteAdminSectorMainImage,
  deleteAdminSectorProjectImage,
  getAdminSectors,
  saveAdminSectorAllLanguages,
  uploadAdminSectorGalleryImage,
  uploadAdminSectorMainImage,
  uploadAdminSectorProjectImage,
} from "../../../api/sectorsAdmin";
import { DashboardReloadButton } from "./dashboardUi";
import { btnAccent, btnDanger, btnPrimary, btnSecondary, langTabActive, langTabIdle } from "./adminProductUi";

const LANGS = ["en", "ar", "ku"];
const LANG_LABELS = { en: "English", ar: "Arabic", ku: "Kurdish" };
const BENEFIT_KEYS = ["interiorWalls", "ceilingSystems", "exteriorCladding"];

const normLang = (value) =>
  String(value || "en")
    .toLowerCase()
    .split("-")[0];

const normKey = (value) => String(value || "").trim().toLowerCase();

const emptyBenefits = () => ({ interiorWalls: [""], ceilingSystems: [""], exteriorCladding: [""] });

/** All translatable content for a single language variant of a sector. */
const emptyLangFields = () => ({
  title: "",
  subtitle: "",
  description: "",
  about: "",
  benefits: emptyBenefits(),
  projectTitle: "",
  projectDescription: "",
});

const emptyTranslations = () => ({
  en: emptyLangFields(),
  ar: emptyLangFields(),
  ku: emptyLangFields(),
});

const EMPTY_FORM = {
  slug: "",
  image: "",
  projectImage: "",
  gallery: [],
  isActive: true,
  translations: emptyTranslations(),
  ids: {},
  primaryId: null,
};

const benefitOrEmpty = (list) =>
  Array.isArray(list) && list.length ? [...list] : [""];

const mapRowToLangFields = (row) => ({
  title: row?.title || "",
  subtitle: row?.subtitle || "",
  description: row?.description || "",
  about: row?.about || "",
  benefits: {
    interiorWalls: benefitOrEmpty(row?.benefits?.interiorWalls),
    ceilingSystems: benefitOrEmpty(row?.benefits?.ceilingSystems),
    exteriorCladding: benefitOrEmpty(row?.benefits?.exteriorCladding),
  },
  projectTitle: row?.project?.title || "",
  projectDescription: row?.project?.description || "",
});

const galleryFromRow = (row) => {
  if (Array.isArray(row?.gallery) && row.gallery.length) {
    return row.gallery.map((g) => ({ id: g?.id ?? null, imageUrl: g?.imageUrl || "" }));
  }
  if (Array.isArray(row?.galleryImages) && row.galleryImages.length) {
    return row.galleryImages.map((url) => ({ id: null, imageUrl: url || "" }));
  }
  return [];
};

/** Build one form (all languages) from the sibling rows of one sector slug. */
const mapSectorsToForm = (rows) => {
  const list = Array.isArray(rows) ? rows.filter(Boolean) : [];
  const primary = list.find((r) => normLang(r?.language) === "en") || list[0] || {};
  const translations = emptyTranslations();
  const ids = {};
  list.forEach((row) => {
    const lang = normLang(row?.language);
    if (!LANGS.includes(lang)) return;
    translations[lang] = mapRowToLangFields(row);
    ids[lang] = row?.id ?? null;
  });
  const gallerySource =
    galleryFromRow(primary).length ? galleryFromRow(primary) : galleryFromRow(list.find((r) => galleryFromRow(r).length));
  return {
    slug: primary?.slug || "",
    image: primary?.image || list.find((r) => r?.image)?.image || "",
    projectImage: primary?.project?.image || list.find((r) => r?.project?.image)?.project?.image || "",
    gallery: gallerySource || [],
    isActive: typeof primary?.isActive === "boolean" ? primary.isActive : true,
    translations,
    ids,
    primaryId: primary?.id ?? list[0]?.id ?? null,
  };
};

const langHasContent = (f) => Boolean((f?.title || "").trim());

const cleanList = (list) => (list || []).map((x) => String(x || "").trim()).filter(Boolean);

const toLanguageContent = (lang, fields) => ({
  language: lang,
  title: (fields.title || "").trim(),
  subtitle: (fields.subtitle || "").trim(),
  description: (fields.description || "").trim(),
  about: (fields.about || "").trim(),
  benefits: {
    interiorWalls: cleanList(fields.benefits?.interiorWalls),
    ceilingSystems: cleanList(fields.benefits?.ceilingSystems),
    exteriorCladding: cleanList(fields.benefits?.exteriorCladding),
  },
  projectTitle: (fields.projectTitle || "").trim(),
  projectDescription: (fields.projectDescription || "").trim(),
});

/** Build the multi-language save payload. Incomplete languages are omitted. */
const buildSavePayload = (form) => ({
  slug: (form.slug || "").trim(),
  image: (form.image || "").trim() || null,
  projectImage: (form.projectImage || "").trim() || null,
  isActive: Boolean(form.isActive),
  galleryImages: (form.gallery || [])
    .map((g) => String(g?.imageUrl || "").trim())
    .filter(Boolean),
  languages: LANGS.filter((lang) => langHasContent(form.translations?.[lang])).map((lang) =>
    toLanguageContent(lang, form.translations[lang])
  ),
});

const SectorsManager = () => {
  const { notify } = useNotification();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [createForm, setCreateForm] = useState(EMPTY_FORM);
  const [editForm, setEditForm] = useState(EMPTY_FORM);
  const [editingKey, setEditingKey] = useState(null);
  const [activeCreateLang, setActiveCreateLang] = useState("en");
  const [activeEditLang, setActiveEditLang] = useState("en");
  const [createMainFile, setCreateMainFile] = useState(null);
  const [createProjectFile, setCreateProjectFile] = useState(null);
  const [createGalleryFiles, setCreateGalleryFiles] = useState([]);
  const [editMainFile, setEditMainFile] = useState(null);
  const [editProjectFile, setEditProjectFile] = useState(null);
  const [editGalleryFiles, setEditGalleryFiles] = useState([]);

  const loadItems = async () => {
    setLoading(true);
    try {
      const data = await getAdminSectors();
      setItems(Array.isArray(data) ? data : []);
    } catch (error) {
      notify({ type: "error", title: "Failed to load sectors", message: error?.response?.data?.message || error?.message || "" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadItems();
  }, []);

  // One card per sector slug; each card bundles all language variants.
  const groupedItems = useMemo(() => {
    const groups = new Map();
    items.forEach((item) => {
      const key = normKey(item?.slug) || `#${item?.id}`;
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(item);
    });
    return [...groups.values()]
      .map((rows) => {
        const rep = rows.find((r) => normLang(r?.language) === "en") || rows[0];
        return {
          key: normKey(rep?.slug) || `#${rep?.id}`,
          rep,
          rows,
          languages: LANGS.filter((lang) => rows.some((r) => normLang(r?.language) === lang)),
        };
      })
      .sort((a, b) => Number(a?.rep?.id || 0) - Number(b?.rep?.id || 0));
  }, [items]);

  const refreshSlug = async (slug) => {
    await loadItems();
    if (slug) {
      const fresh = (await getAdminSectors().catch(() => [])).filter(
        (b) => normKey(b?.slug) === normKey(slug)
      );
      setEditForm(mapSectorsToForm(fresh));
    }
  };

  const uploadPendingFiles = async (repId, { mainFile, projectFile, galleryFiles }) => {
    if (!repId) return;
    if (mainFile) await uploadAdminSectorMainImage({ id: repId, file: mainFile });
    if (projectFile) await uploadAdminSectorProjectImage({ id: repId, file: projectFile });
    for (const file of galleryFiles || []) {
      // eslint-disable-next-line no-await-in-loop
      await uploadAdminSectorGalleryImage({ id: repId, file });
    }
  };

  const handleCreate = async () => {
    const anyLang = LANGS.some((lang) => langHasContent(createForm.translations[lang]));
    if (!createForm.slug.trim() || !anyLang) {
      notify({ type: "error", title: "Slug and at least one language with a Title are required" });
      return;
    }
    setCreating(true);
    try {
      const payload = buildSavePayload(createForm);
      const savedRows = await saveAdminSectorAllLanguages(payload);
      const rep =
        (Array.isArray(savedRows) && (savedRows.find((b) => normLang(b?.language) === "en") || savedRows[0])) || null;
      await uploadPendingFiles(rep?.id, {
        mainFile: createMainFile,
        projectFile: createProjectFile,
        galleryFiles: createGalleryFiles,
      });
      notify({ type: "success", title: "Sector saved for all languages" });
      setCreateForm(EMPTY_FORM);
      setCreateMainFile(null);
      setCreateProjectFile(null);
      setCreateGalleryFiles([]);
      setActiveCreateLang("en");
      await loadItems();
    } catch (error) {
      notify({ type: "error", title: "Save failed", message: error?.response?.data?.message || error?.message || "" });
    } finally {
      setCreating(false);
    }
  };

  const startEdit = (group) => {
    const key = normKey(group?.rep?.slug) || group.key;
    setEditingKey(key);
    setActiveEditLang("en");
    setEditMainFile(null);
    setEditProjectFile(null);
    setEditGalleryFiles([]);
    setEditForm(mapSectorsToForm(group.rows));
  };

  const saveEdit = async () => {
    if (!editingKey) return;
    const anyLang = LANGS.some((lang) => langHasContent(editForm.translations[lang]));
    if (!editForm.slug.trim() || !anyLang) {
      notify({ type: "error", title: "Slug and at least one language with a Title are required" });
      return;
    }
    setSaving(true);
    try {
      const payload = buildSavePayload(editForm);
      const savedRows = await saveAdminSectorAllLanguages(payload);
      const rep =
        (Array.isArray(savedRows) && (savedRows.find((b) => normLang(b?.language) === "en") || savedRows[0])) || null;
      await uploadPendingFiles(rep?.id, {
        mainFile: editMainFile,
        projectFile: editProjectFile,
        galleryFiles: editGalleryFiles,
      });
      notify({ type: "success", title: "Sector updated for all languages" });
      setEditingKey(null);
      setEditForm(EMPTY_FORM);
      setEditMainFile(null);
      setEditProjectFile(null);
      setEditGalleryFiles([]);
      await loadItems();
    } catch (error) {
      notify({ type: "error", title: "Update failed", message: error?.response?.data?.message || error?.message || "" });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (group) => {
    if (!window.confirm(`Delete sector "${group?.rep?.title || group?.rep?.slug}" and all its language versions?`)) return;
    try {
      for (const row of group.rows) {
        // eslint-disable-next-line no-await-in-loop
        await deleteAdminSector({ id: row.id });
      }
      notify({ type: "success", title: "Sector deleted" });
      await loadItems();
    } catch (error) {
      notify({ type: "error", title: "Delete failed", message: error?.response?.data?.message || error?.message || "" });
    }
  };

  const closeEdit = () => {
    setEditingKey(null);
    setEditForm(EMPTY_FORM);
    setEditMainFile(null);
    setEditProjectFile(null);
    setEditGalleryFiles([]);
    setActiveEditLang("en");
  };

  const editRepId = editForm.ids?.en ?? editForm.primaryId ?? null;

  const removeMainImage = async () => {
    if (!editRepId) return;
    try {
      await deleteAdminSectorMainImage({ id: editRepId });
      notify({ type: "success", title: "Main image removed" });
      await refreshSlug(editForm.slug);
    } catch (error) {
      notify({ type: "error", title: "Remove failed", message: error?.response?.data?.message || error?.message || "" });
    }
  };

  const removeProjectImage = async () => {
    if (!editRepId) return;
    try {
      await deleteAdminSectorProjectImage({ id: editRepId });
      notify({ type: "success", title: "Project image removed" });
      await refreshSlug(editForm.slug);
    } catch (error) {
      notify({ type: "error", title: "Remove failed", message: error?.response?.data?.message || error?.message || "" });
    }
  };

  const removeGalleryImage = async (img, idx) => {
    if (img?.id && editRepId) {
      try {
        await deleteAdminSectorGalleryImage({ id: editRepId, imageId: img.id });
        notify({ type: "success", title: "Gallery image removed" });
        await refreshSlug(editForm.slug);
      } catch (error) {
        notify({ type: "error", title: "Remove failed", message: error?.response?.data?.message || error?.message || "" });
      }
      return;
    }
    setEditForm((prev) => ({ ...prev, gallery: (prev.gallery || []).filter((_, i) => i !== idx) }));
  };

  const renderImageBlock = (label, form, setForm, urlKey, { isEdit, file, setFile, onRemove }) => {
    const url = form[urlKey];
    return (
      <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-3">
        <p className="text-sm font-semibold text-slate-900 mb-2">{label} (shared across languages)</p>
        <div className="flex flex-col sm:flex-row gap-3 items-start">
          {resolveMediaUrl(url) ? (
            <img src={resolveMediaUrl(url)} alt="" className="w-full sm:w-40 h-24 object-cover rounded-lg border border-slate-200 bg-white" />
          ) : (
            <div className="w-full sm:w-40 h-24 rounded-lg border border-dashed border-slate-300 bg-white flex items-center justify-center text-xs text-slate-400">
              No image
            </div>
          )}
          <div className="flex-1 space-y-2 min-w-0 w-full">
            <input
              type="text"
              value={url}
              onChange={(e) => setForm((prev) => ({ ...prev, [urlKey]: e.target.value }))}
              placeholder="/uploads/... or https://..."
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white"
            />
            <input
              type="file"
              accept="image/*"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="block w-full text-sm"
            />
            {file?.name ? (
              <p className="text-xs text-slate-500">Selected: {file.name} — uploads when you save.</p>
            ) : null}
            {isEdit && url ? (
              <button type="button" className={btnDanger} onClick={onRemove}>
                Remove image
              </button>
            ) : null}
          </div>
        </div>
      </div>
    );
  };

  const renderSharedFields = (form, setForm, { isEdit = false, mainFile, setMainFile, projectFile, setProjectFile, galleryFiles, setGalleryFiles } = {}) => (
    <div className="grid gap-3">
      <div className="grid sm:grid-cols-2 gap-3">
        <label className="text-sm text-slate-700">
          Slug (shared across languages)
          <input
            type="text"
            value={form.slug}
            readOnly={isEdit}
            onChange={(e) => setForm((prev) => ({ ...prev, slug: e.target.value }))}
            className={`mt-1 w-full rounded-lg border px-3 py-2 text-sm ${
              isEdit ? "border-slate-200 bg-slate-100 text-slate-500" : "border-slate-300 bg-white"
            }`}
          />
          {isEdit ? <span className="mt-1 block text-xs text-slate-400">Shared and cannot be changed here.</span> : null}
        </label>
        <label className="inline-flex items-center gap-2 text-sm text-slate-700">
          <input
            type="checkbox"
            checked={Boolean(form.isActive)}
            onChange={(e) => setForm((prev) => ({ ...prev, isActive: e.target.checked }))}
          />
          Active
        </label>
      </div>

      {renderImageBlock("Main image", form, setForm, "image", {
        isEdit,
        file: mainFile,
        setFile: setMainFile,
        onRemove: removeMainImage,
      })}
      {renderImageBlock("Project image", form, setForm, "projectImage", {
        isEdit,
        file: projectFile,
        setFile: setProjectFile,
        onRemove: removeProjectImage,
      })}

      <div className="rounded-xl border border-slate-200 p-3">
        <p className="text-sm font-semibold text-[#011337] mb-2">Gallery images (shared across languages)</p>
        {(form.gallery || []).length ? (
          <div className="grid sm:grid-cols-3 gap-3">
            {(form.gallery || []).map((img, idx) => (
              <div key={`${img?.id || "u"}-${idx}`} className="rounded border border-slate-200 p-2">
                {resolveMediaUrl(img?.imageUrl) ? (
                  <img src={resolveMediaUrl(img.imageUrl)} alt="" className="w-full h-24 object-cover rounded" />
                ) : (
                  <div className="w-full h-24 rounded bg-slate-100" />
                )}
                <input
                  type="text"
                  value={img?.imageUrl || ""}
                  onChange={(e) =>
                    setForm((prev) => {
                      const next = [...(prev.gallery || [])];
                      next[idx] = { ...(next[idx] || {}), imageUrl: e.target.value };
                      return { ...prev, gallery: next };
                    })
                  }
                  placeholder="/uploads/... or https://..."
                  className="mt-2 w-full rounded border border-slate-300 px-2 py-1 text-xs"
                />
                <button
                  type="button"
                  onClick={() => removeGalleryImage(img, idx)}
                  className="mt-1 text-xs px-2 py-1 rounded border border-rose-200 text-rose-700 hover:bg-rose-50"
                >
                  Delete
                </button>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-xs text-slate-500">No gallery images yet.</p>
        )}
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={() => setForm((prev) => ({ ...prev, gallery: [...(prev.gallery || []), { id: null, imageUrl: "" }] }))}
            className="text-xs px-3 py-1.5 rounded border border-slate-300 hover:bg-slate-50"
          >
            Add gallery URL
          </button>
          <label className="text-sm text-slate-700">
            <span className="text-xs text-slate-500">Upload gallery image(s)</span>
            <input
              type="file"
              multiple
              accept="image/*"
              onChange={(e) => setGalleryFiles(Array.from(e.target.files || []))}
              className="mt-1 block w-full text-sm"
            />
          </label>
          {galleryFiles?.length ? (
            <span className="text-xs text-slate-500">{galleryFiles.length} file(s) — uploads when you save.</span>
          ) : null}
        </div>
      </div>
    </div>
  );

  const renderLangContent = (form, setForm, lang) => {
    const fields = form.translations[lang] || emptyLangFields();
    const dir = lang === "ar" || lang === "ku" ? "rtl" : "ltr";

    const patch = (partial) =>
      setForm((prev) => ({
        ...prev,
        translations: { ...prev.translations, [lang]: { ...prev.translations[lang], ...partial } },
      }));

    const setBenefit = (key, idx, value) =>
      setForm((prev) => {
        const current = prev.translations[lang].benefits?.[key] || [""];
        const next = [...current];
        next[idx] = value;
        return {
          ...prev,
          translations: {
            ...prev.translations,
            [lang]: {
              ...prev.translations[lang],
              benefits: { ...prev.translations[lang].benefits, [key]: next },
            },
          },
        };
      });

    const mutateBenefit = (key, updater) =>
      setForm((prev) => {
        const current = prev.translations[lang].benefits?.[key] || [""];
        const next = updater([...current]);
        return {
          ...prev,
          translations: {
            ...prev.translations,
            [lang]: {
              ...prev.translations[lang],
              benefits: { ...prev.translations[lang].benefits, [key]: next.length ? next : [""] },
            },
          },
        };
      });

    return (
      <div className="grid gap-3" dir={dir}>
        <div className="grid sm:grid-cols-2 gap-3">
          <label className="text-sm text-slate-700">
            Title ({lang.toUpperCase()})
            <input
              type="text"
              value={fields.title}
              onChange={(e) => patch({ title: e.target.value })}
              className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white"
            />
          </label>
          <label className="text-sm text-slate-700">
            Subtitle ({lang.toUpperCase()})
            <input
              type="text"
              value={fields.subtitle}
              onChange={(e) => patch({ subtitle: e.target.value })}
              className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white"
            />
          </label>
          <label className="text-sm text-slate-700 sm:col-span-2">
            Description ({lang.toUpperCase()})
            <textarea
              value={fields.description}
              onChange={(e) => patch({ description: e.target.value })}
              className="mt-1 w-full min-h-[90px] rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white"
            />
          </label>
          <label className="text-sm text-slate-700 sm:col-span-2">
            About ({lang.toUpperCase()})
            <textarea
              value={fields.about}
              onChange={(e) => patch({ about: e.target.value })}
              className="mt-1 w-full min-h-[90px] rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white"
            />
          </label>
        </div>

        <div className="rounded-xl border border-slate-200 p-3">
          <p className="text-sm font-semibold text-[#011337] mb-2">Benefits ({lang.toUpperCase()})</p>
          {BENEFIT_KEYS.map((key) => (
            <div key={key} className="mb-2">
              <p className="text-xs uppercase text-slate-500 mb-1">{key}</p>
              {(fields.benefits?.[key] || [""]).map((line, idx) => (
                <div key={`${key}-${idx}`} className="flex gap-2 mb-1">
                  <input
                    type="text"
                    value={line}
                    onChange={(e) => setBenefit(key, idx, e.target.value)}
                    className="flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm"
                  />
                  <button
                    type="button"
                    onClick={() => mutateBenefit(key, (next) => next.filter((_, i) => i !== idx))}
                    className="px-2 rounded border border-rose-200 text-rose-700"
                  >
                    -
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={() => mutateBenefit(key, (next) => [...next, ""])}
                className="text-xs px-2 py-1 rounded border border-slate-300"
              >
                Add line
              </button>
            </div>
          ))}
        </div>

        <div className="rounded-xl border border-slate-200 p-3">
          <p className="text-sm font-semibold text-[#011337] mb-2">Project block ({lang.toUpperCase()})</p>
          <div className="grid gap-2">
            <input
              type="text"
              value={fields.projectTitle}
              onChange={(e) => patch({ projectTitle: e.target.value })}
              className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
              placeholder="Project title"
            />
            <textarea
              value={fields.projectDescription}
              onChange={(e) => patch({ projectDescription: e.target.value })}
              className="rounded-lg border border-slate-300 px-3 py-2 text-sm min-h-[70px]"
              placeholder="Project description"
            />
          </div>
        </div>
      </div>
    );
  };

  const renderLangTabs = (activeLang, setActiveLang, form, keyPrefix) => (
    <div className="flex flex-wrap items-center gap-2">
      {LANGS.map((lang) => {
        const hasContent = langHasContent(form.translations?.[lang]);
        return (
          <button
            key={`${keyPrefix}-${lang}`}
            type="button"
            onClick={() => setActiveLang(lang)}
            className={activeLang === lang ? langTabActive : langTabIdle}
          >
            {LANG_LABELS[lang]}
            {hasContent ? " ●" : ""}
          </button>
        );
      })}
    </div>
  );

  return (
    <section className="space-y-4">
      <div className="flex justify-end">
        <DashboardReloadButton onClick={loadItems} loading={loading} label="Reload list" />
      </div>

      <AdminCollapsibleSection
        title="Add new sector"
        description="Fill in every language (English, Arabic, Kurdish). All languages are saved together."
        badge="Create"
        footer={
          <button type="button" disabled={creating} onClick={handleCreate} className={btnAccent}>
            {creating ? "Saving..." : "Save sector (all languages)"}
          </button>
        }
      >
        <div className="grid gap-4">
          {renderSharedFields(createForm, setCreateForm, {
            mainFile: createMainFile,
            setMainFile: setCreateMainFile,
            projectFile: createProjectFile,
            setProjectFile: setCreateProjectFile,
            galleryFiles: createGalleryFiles,
            setGalleryFiles: setCreateGalleryFiles,
          })}
          {renderLangTabs(activeCreateLang, setActiveCreateLang, createForm, "create")}
          {renderLangContent(createForm, setCreateForm, activeCreateLang)}
        </div>
      </AdminCollapsibleSection>

      <div>
        <div className="flex flex-wrap items-baseline justify-between gap-2 mb-4">
          <h3 className="text-lg font-semibold text-[#011337]">Your sectors</h3>
          {!loading ? (
            <span className="text-sm text-slate-500">
              {groupedItems.length} item{groupedItems.length !== 1 ? "s" : ""}
            </span>
          ) : null}
        </div>
        {loading ? (
          <p className="text-sm text-slate-500 py-8 text-center rounded-xl border border-slate-200 bg-white">
            Loading sectors...
          </p>
        ) : groupedItems.length ? (
          <div className="grid gap-3">
            {groupedItems.map((group) => {
              const missing = LANGS.filter((lang) => !group.languages.includes(lang));
              return (
                <AdminProductListCard
                  key={group.key}
                  id={group.rep?.id}
                  title={group.rep?.title || group.rep?.slug}
                  meta={[
                    group.rep?.slug ? `Slug: ${group.rep.slug}` : null,
                    `Languages: ${group.languages.map((l) => l.toUpperCase()).join(", ") || "—"}`,
                    missing.length ? `Missing: ${missing.map((l) => l.toUpperCase()).join(", ")}` : null,
                  ].filter(Boolean)}
                  status={typeof group.rep?.isActive === "boolean" ? group.rep.isActive : null}
                  imageUrl={group.rep?.image}
                  isEditing={editingKey === group.key}
                  onEdit={() => startEdit(group)}
                  onDelete={() => handleDelete(group)}
                />
              );
            })}
          </div>
        ) : (
          <p className="text-sm text-slate-500 py-8 text-center rounded-xl border border-dashed border-slate-300 bg-slate-50">
            No sectors found. Use &quot;Add new sector&quot; above.
          </p>
        )}
      </div>

      <AdminEditorModal
        open={Boolean(editingKey)}
        title={`Edit sector: ${editForm.translations?.en?.title || editForm.slug || editingKey || ""}`}
        subtitle="Edit every language, then save. Changes apply to the public sector pages."
        onClose={closeEdit}
        footer={
          <>
            <button type="button" onClick={closeEdit} className={btnSecondary}>
              Cancel
            </button>
            <button type="button" onClick={saveEdit} disabled={saving} className={btnPrimary}>
              {saving ? "Saving..." : "Save changes"}
            </button>
          </>
        }
      >
        <div className="grid gap-4">
          {renderSharedFields(editForm, setEditForm, {
            isEdit: true,
            mainFile: editMainFile,
            setMainFile: setEditMainFile,
            projectFile: editProjectFile,
            setProjectFile: setEditProjectFile,
            galleryFiles: editGalleryFiles,
            setGalleryFiles: setEditGalleryFiles,
          })}
          {renderLangTabs(activeEditLang, setActiveEditLang, editForm, "edit")}
          {renderLangContent(editForm, setEditForm, activeEditLang)}
        </div>
      </AdminEditorModal>
    </section>
  );
};

export default SectorsManager;
